import classNames from "classnames";
import { FC, useState } from "react";
import { useI18n } from "../../hooks/useTranslations";
import SvgClose from "../icons/Close";
import SvgPaperClip from "../icons/PaperClip";
import Button from "./Button";
import FileSelector from "./FileSelector";
import styles from "./FileUploader.module.css";
import Icon from "./Icon";
import LoadingSpinner from "./LoadingSpinner";
import SubmitBar from "./SubmitBar";

interface FileUploaderPropsBase {
  onCancel?: () => void;
  disabled?: boolean;
  isSubmitting?: boolean;
  accept?: string;
}

// Single-file variant
interface FileUploaderSingleProps extends FileUploaderPropsBase {
  allowMultiSelection?: false;
  onSave?: (file: File) => void;
  onChange?: (file: File) => void;
}

// Multi-file variant
interface FileUploaderMultiProps extends FileUploaderPropsBase {
  allowMultiSelection: true;
  onSave?: (files: File[]) => void;
  onChange?: (files: File[]) => void;
}

// Union of both variants
type FileUploaderProps = FileUploaderSingleProps | FileUploaderMultiProps;

type FileWithPreview = File & { isPreviewable?: boolean; preview?: string };

const FileUploader: FC<FileUploaderProps> = ({
  onCancel,
  onSave,
  onChange,
  disabled,
  isSubmitting,
  allowMultiSelection,
  accept,
}: FileUploaderProps) => {
  const i18n = useI18n();

  const [file, setFile] = useState<File>();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState(false);

  const handleFileSelected = (newFile: File) => {
    setError(false);
    if (allowMultiSelection) {
      const isPreviewable = isPreviewableImage(newFile);

      setFiles((prev) => {
        const updatedFiles = [
          ...prev,
          Object.assign(newFile, {
            isPreviewable,
            preview: isPreviewable ? URL.createObjectURL(newFile) : undefined,
          }),
        ];

        onChange?.(updatedFiles);
        return updatedFiles;
      });
    } else {
      setFile(newFile);
      onChange && onChange(newFile);
    }
  };

  const save = () => {
    if (allowMultiSelection && files.length === 0) {
      setError(true);
      return;
    }
    if (!allowMultiSelection && !file) {
      setError(true);
      return;
    }

    if (onSave) {
      // Call onSave depending on mode
      allowMultiSelection ? onSave(files) : onSave(file!);
    }
  };

  return (
    <div>
      {allowMultiSelection && !!files.length && (
        <div className={styles["uploader-items"]}>
          {files.map((f, i) => (
            <div className={styles["uploader-item"]} key={`_thumb_${i}`}>
              <div
                className={classNames(
                  styles["uploader-item-preview"],
                  !f.isPreviewable ? styles.background : undefined,
                )}
              >
                {f.isPreviewable ? <img src={f.preview} /> : <Icon glyph={SvgPaperClip} />}
              </div>
              <div className={styles["uploader-item-panel"]}>
                <div>{f.name}</div>
                <div className={styles["uploader-item-panel-size"]}>{f.size} kb</div>
              </div>
              <div
                className={styles["uploader-item-remove-button"]}
                onClick={() => {
                  URL.revokeObjectURL(f.preview!);
                  setFiles((prev) => {
                    const updatedFiles = prev.filter((x) => x !== f);
                    onChange?.(updatedFiles);
                    return updatedFiles;
                  });
                }}
              >
                <Icon glyph={SvgClose} />
              </div>
            </div>
          ))}
        </div>
      )}
      <FileSelector
        onFileSelected={handleFileSelected}
        isInvalid={error}
        allowMultiSelection={allowMultiSelection}
        accept={accept}
        disabled={disabled}
      />
      {onSave ? (
        <SubmitBar>
          {!!isSubmitting && <LoadingSpinner />}
          {onCancel ? (
            <Button type="secondary" buttonProps={{ disabled: disabled, onClick: () => onCancel() }}>
              {i18n.t.common.cancel}
            </Button>
          ) : undefined}
          <Button buttonProps={{ disabled: disabled, onClick: save }}>{i18n.t.common.save}</Button>
        </SubmitBar>
      ) : undefined}
    </div>
  );
};

export default FileUploader;

function isPreviewableImage(file: File) {
  return file.type.startsWith("image/");
}
